import { Box } from "@mui/material";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { Layout, PlotData } from "plotly.js";
import { useMemo } from "react";
import Plot from "components/plotly/LocalisedPlot";
import { LegendHint } from "../LegendHint";
import { UnitType } from "src/store/api/generatedApi";
import {
  defaultLayout,
  generalTickFormatting,
  getColorBySource,
  setSelectedDateFromPlotClick,
} from "../plotting";
import { GroupedDeliverySeries } from "../types";
import { TFunction } from "i18next";

interface DeliveriesOverviewPlotProps {
  deliverySeries: GroupedDeliverySeries;
  selectedMaterials: string[];
  setSelectedDate: (date: Date) => void;
}

export const DeliveriesOverviewPlot = ({
  deliverySeries,
  selectedMaterials,
  setSelectedDate,
}: DeliveriesOverviewPlotProps) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);

  const figure = useMemo(
    () => ({
      data: buildFigData(deliverySeries, selectedMaterials, t),
      layout: buildLayout(defaultLayout, t, units),
    }),
    [deliverySeries, selectedMaterials, t, units]
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Plot
        data={figure.data}
        layout={figure.layout}
        style={{ width: "100%" }}
        useResizeHandler
        onClick={setSelectedDateFromPlotClick(setSelectedDate)}
      />
      <LegendHint />
    </Box>
  );
};

const buildFigData = (
  deliverySeries: GroupedDeliverySeries,
  selectedMaterials: string[],
  t: TFunction
): Partial<PlotData>[] => {
  const materialNames = deliverySeries.availableMaterialNames;

  return deliverySeries.values
    .filter(([key]) => selectedMaterials.includes(key.materialName))
    .map(([key, series]) => {
      const colour = getColorBySource(materialNames, key.materialName);
      return {
        x: series.date,
        y: series.deliveryMassMtd,
        type: "scatter",
        mode: "lines+markers",
        name: t(key.materialName),
        line: { color: colour },
        marker: { color: colour },
        stackgroup: "deliveries",
      };
    });
};

const buildLayout = (
  baseLayout: Partial<Layout>,
  t: TFunction,
  units: (key: UnitType) => string
): Partial<Layout> => ({
  ...baseLayout,
  legend: {
    ...baseLayout.legend,
    traceorder: "normal",
  },
  xaxis: {
    title: { text: t("date") },
  },
  yaxis: {
    title: { text: `${t("deliveries")}${units("mass")}` },
    tickformat: generalTickFormatting,
  },
});
